import { FC } from "react"
import cx from "classnames"
import { useFormik } from "formik"
import validationSchema from "./validationSchema"
import Modal, { ModalProps } from "@/components/Modal"
import OutlineButton from "@/components/OutlineButton"
import TextField from "@/components/TextField"
import Button from "@/components/Button"
import Select from "@/components/Select"
import { useGetSystemValuesQuery } from "@/redux/services/generalApi"
import { useAddAdminProxy } from "../../hooks/useAddProxy"
import { useEditAdminProxy } from "../../hooks/useEditProxy"
import type { Proxy } from "@/types/entities/proxy"

type Props = {
  className?: string
  data?: Proxy
} & ModalProps

const AddEditProxyModal: FC<Props> = ({
  className,
  open,
  onClose,
  data,
  ...rest
}: Props) => {
  const isEdit = !!data
  const { data: { regions } = { regions: [] }, isLoading: valuesLoading } =
    useGetSystemValuesQuery()
  const {
    addAdminProxy,
    response: { isLoading: addLoading },
  } = useAddAdminProxy()
  const {
    editAdminProxy,
    response: { isLoading: editLoading },
  } = useEditAdminProxy()
  const {
    getFieldProps,
    handleSubmit,
    resetForm,
    isValid,
    dirty,
    setFieldValue,
    values: { region },
  } = useFormik({
    enableReinitialize: true,
    initialValues: {
      ip: data?.ip ?? "",
      port: data?.port ?? "",
      username: data?.username ?? "",
      password: data?.password ?? "",
      region: data?.region ?? "",
    },
    validationSchema,
    onSubmit: async (values) => {
      const { port } = values
      const body = {
        ...values,
        port: typeof port === "number" ? port : parseInt(port),
      }
      try {
        isEdit
          ? await editAdminProxy({
              id: data._id,
              body,
            })
          : await addAdminProxy(body)
        handleClose()
      } catch (e) {
        console.error(e)
      }
    },
  })
  const handleClose = () => {
    resetForm()
    onClose && onClose()
  }
  const submitDisabled = addLoading || editLoading || !(isValid && dirty)
  const submitBtnText = isEdit ? "Save" : "Add"
  const title = isEdit ? "Edit Proxy" : "Add Proxy"

  return (
    <Modal
      title={title}
      open={open}
      onClose={handleClose}
      onSubmit={handleSubmit}
      className={cx("", className)}
      actions={
        <div className="flex gap-4">
          <OutlineButton
            type="button"
            className="btn-sm text-black min-w-[86px]"
            onClick={handleClose}
            disabled={addLoading || editLoading}
          >
            Cancel
          </OutlineButton>
          <Button
            disabled={submitDisabled}
            type="submit"
            className="btn-sm min-w-[86px]"
          >
            {submitBtnText}
          </Button>
        </div>
      }
      {...rest}
    >
      <div className="space-y-5">
        <TextField
          topLeftLabel="IP Address"
          placeholder="Enter IP address"
          className="input-sm"
          {...getFieldProps("ip")}
        />
        <TextField
          topLeftLabel="Port"
          placeholder="8888"
          className="input-sm"
          type="number"
          {...getFieldProps("port")}
        />
        <TextField
          topLeftLabel="User"
          placeholder="Proxy user"
          className="input-sm"
          autoComplete="off"
          {...getFieldProps("username")}
        />
        <TextField
          topLeftLabel="Password"
          placeholder="Enter password"
          className="input-sm"
          type="password"
          autoComplete="new-password"
          {...getFieldProps("password")}
        />
        <Select
          value={region || null}
          onChange={(v) => setFieldValue("region", v)}
          label="Region"
          placeholder="Select region"
          className="h-10 bg-input rounded-md"
          bordered={false}
          options={regions.map(({ id, name }) => ({
            label: name,
            value: id,
          }))}
          getPopupContainer={(node) => node.parentNode}
          loading={valuesLoading}
        />
      </div>
    </Modal>
  )
}

export default AddEditProxyModal
